/**
 * Telephone keypad tones (DTMF), synthesized with the Web Audio API — no audio files. Each key is two
 * sine waves at the standard row/column frequencies. Used by the dialer before a call; during a call
 * the Twilio SDK plays its own tones when digits are sent.
 */
const ROWS = [697, 770, 852, 941];
const COLS = [1209, 1336, 1477];
const LAYOUT = ["123", "456", "789", "*0#"];
const DURATION = 0.12; // seconds
const VOLUME = 0.08;

let ctx: AudioContext | null = null;

function frequencies(key: string): [number, number] | null {
  const row = LAYOUT.findIndex((r) => r.includes(key));
  if (row < 0) return null;
  return [ROWS[row]!, COLS[LAYOUT[row]!.indexOf(key)]!];
}

export const isToneKey = (key: string) => frequencies(key) !== null;

export function playTone(key: string) {
  const freqs = frequencies(key);
  if (!freqs || typeof window === "undefined" || !("AudioContext" in window)) return;
  try {
    ctx ??= new AudioContext();
    if (ctx.state === "suspended") void ctx.resume();
    const now = ctx.currentTime;
    const gain = ctx.createGain();
    // Short fade in/out so the tone doesn't click.
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(VOLUME, now + 0.005);
    gain.gain.setValueAtTime(VOLUME, now + DURATION - 0.015);
    gain.gain.linearRampToValueAtTime(0, now + DURATION);
    gain.connect(ctx.destination);
    for (const f of freqs) {
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.value = f;
      osc.connect(gain);
      osc.start(now);
      osc.stop(now + DURATION);
    }
  } catch {
    // Audio unavailable (e.g. blocked autoplay): the keypad still works silently.
  }
}
